import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import AppLayout from '../components/AppLayout';
import AdminLayout from '../components/AdminLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import { ROLES, ADMIN_ROLES } from '../utils/roles';

function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch(() => {
      const hasReloaded = sessionStorage.getItem('lazy_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('lazy_reload', '1');
        window.location.reload();
        return new Promise(() => {});
      }
      sessionStorage.removeItem('lazy_reload');
      return importFn().catch(() => ({
        default: function ChunkLoadError() {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '24px', textAlign: 'center' }}>
              <p style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '12px' }}>تعذّر تحميل الصفحة</p>
              <p style={{ color: '#6b7280', marginBottom: '20px' }}>تحقق من اتصالك بالإنترنت ثم حاول مجدداً.</p>
              <button onClick={() => window.location.href = '/'} style={{ padding: '10px 24px', borderRadius: '8px', background: '#6366f1', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                العودة للرئيسية
              </button>
            </div>
          );
        },
      }));
    })
  );
}

const Login = lazyRetry(() => import('../pages/Login'));
const Dashboard = lazyRetry(() => import('../pages/admin/Dashboard'));

const DecksAdmin = lazyRetry(() => import('../pages/decks/Admin'));
const UnifiedDeckEditor = lazyRetry(() => import('../pages/decks/DeckEditor'));

const EmbedRouter = lazyRetry(() => import('../pages/embed/EmbedRouter'));
const EmbedPlayer = lazyRetry(() => import('../pages/flashcards/EmbedPlayer'));
const FlashQuestionsEmbedPlayer = lazyRetry(() => import('../pages/drills/EmbedPlayer'));

function RedirectDeckEditor() {
  const { deckId } = useParams();
  return <Navigate to={`/decks/admin/${deckId}`} replace />;
}
function RedirectQuizEditor() {
  const { quizId } = useParams();
  return <Navigate to={`/decks/admin/${quizId}`} replace />;
}

const ErrorReports = lazyRetry(() => import('../pages/ErrorReports'));
const UserManagement = lazyRetry(() => import('../pages/admin/UserManagement'));
const StudentManagement = lazyRetry(() => import('../pages/admin/StudentManagement'));
const ClassManagement = lazyRetry(() => import('../pages/admin/ClassManagement'));

const SiteSettings = lazyRetry(() => import('../pages/admin/SiteSettings'));
const StudentStats = lazyRetry(() => import('../pages/admin/StudentStats'));
const StudentStatsDetail = lazyRetry(() => import('../pages/admin/StudentStatsDetail'));
const NotificationSettings = lazyRetry(() => import('../pages/admin/NotificationSettings'));
const MonitoringDashboard = lazyRetry(() => import('../pages/admin/MonitoringDashboard'));
const SubjectStats = lazyRetry(() => import('../pages/admin/SubjectStats'));
const SubjectStatsDetail = lazyRetry(() => import('../pages/admin/SubjectStatsDetail'));
const JourneysAdmin = lazyRetry(() => import('../pages/journeys/Admin'));
const JourneyEditor = lazyRetry(() => import('../pages/journeys/JourneyEditor'));

const JourneyPreview     = lazyRetry(() => import('../pages/demo/JourneyPreview'));
const StudentHomePreview = lazyRetry(() => import('../pages/demo/StudentHomePreview'));

const PublicHome  = lazyRetry(() => import('../pages/PublicHome'));
const StudentHome = lazyRetry(() => import('../pages/student/StudentHome'));
const StudentDeckPlayer = lazyRetry(() => import('../pages/student/StudentDeckPlayer'));
const StudentQuizPlayer = lazyRetry(() => import('../pages/student/StudentQuizPlayer'));
const TrainSession = lazyRetry(() => import('../pages/student/TrainSession'));
const StudentJourneyList = lazyRetry(() => import('../pages/student/StudentJourneyList'));
const StudentJourneyPage = lazyRetry(() => import('../pages/student/StudentJourneyPage'));
const JourneyStepRunner = lazyRetry(() => import('../pages/student/journey/JourneyStepRunner'));
const JourneyTamkeenSession = lazyRetry(() => import('../pages/student/journey/JourneyTamkeenSession'));
const StudentFavorites = lazyRetry(() => import('../pages/student/Favorites'));
const StudentProfile = lazyRetry(() => import('../pages/student/Profile'));

const adminUp = [ROLES.SUPER_ADMIN, ROLES.ADMIN];

function SuspenseWrap({ children }) {
  return <Suspense fallback={<div className="loading-screen">جاري التحميل...</div>}>{children}</Suspense>;
}

function RootPage() {
  const { user, role, loading } = useAuth();
  if (loading) return <div className="loading-screen">جاري التحميل...</div>;
  if (!user) return <SuspenseWrap><PublicHome /></SuspenseWrap>;
  if (role === ROLES.STUDENT) return <SuspenseWrap><StudentHome /></SuspenseWrap>;
  if (role === ROLES.EDITOR) return <Navigate to="/decks/admin" replace />;
  if (role) return <Navigate to="/admin" replace />;
  return <Navigate to="/login" replace />;
}

export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      { path: '/', element: <RootPage /> },
      { path: '/login', element: <SuspenseWrap><Login /></SuspenseWrap> },
      { path: '/demo/journey', element: <SuspenseWrap><JourneyPreview /></SuspenseWrap> },
      { path: '/demo/home',    element: <SuspenseWrap><StudentHomePreview /></SuspenseWrap> },

      /* ── Admin routes — wrapped in AdminLayout (sidebar + topbar) ── */
      {
        element: <AdminLayout />,
        children: [
          { path: '/admin', element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><Dashboard /></SuspenseWrap></ProtectedRoute> },
          {
            path: '/admin/reports',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><ErrorReports /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/users',
            element: <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN]}><SuspenseWrap><UserManagement /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/students',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><StudentManagement /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/classes',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><ClassManagement /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/settings',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><SiteSettings /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/notifications',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><NotificationSettings /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/monitoring',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><MonitoringDashboard /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/student-stats',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><StudentStats /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/student-stats/:uid',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><StudentStatsDetail /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/subject-stats',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><SubjectStats /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/admin/subject-stats/:classId',
            element: <ProtectedRoute allowedRoles={adminUp}><SuspenseWrap><SubjectStatsDetail /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/decks/admin',
            element: <ProtectedRoute allowedRoles={ADMIN_ROLES}><SuspenseWrap><DecksAdmin /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/decks/admin/:deckId',
            element: <ProtectedRoute allowedRoles={ADMIN_ROLES}><SuspenseWrap><UnifiedDeckEditor /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/journeys/admin',
            element: <ProtectedRoute allowedRoles={ADMIN_ROLES}><SuspenseWrap><JourneysAdmin /></SuspenseWrap></ProtectedRoute>,
          },
          {
            path: '/journeys/admin/:journeyId',
            element: <ProtectedRoute allowedRoles={ADMIN_ROLES}><SuspenseWrap><JourneyEditor /></SuspenseWrap></ProtectedRoute>,
          },
        ],
      },

      /* ── Legacy redirects ── */
      { path: '/flashcards/admin', element: <Navigate to="/decks/admin" replace /> },
      { path: '/flashcards/admin/deck/:deckId', element: <RedirectDeckEditor /> },
      { path: '/drills/admin', element: <Navigate to="/decks/admin" replace /> },
      { path: '/drills/admin/quiz/:quizId', element: <RedirectQuizEditor /> },

      /* ── Embeds ── */
      { path: '/embed', element: <SuspenseWrap><EmbedRouter /></SuspenseWrap> },
      { path: '/flashcards/embed', element: <SuspenseWrap><EmbedPlayer /></SuspenseWrap> },
      { path: '/drills/embed', element: <SuspenseWrap><FlashQuestionsEmbedPlayer /></SuspenseWrap> },
      { path: '/mix/embed', element: <SuspenseWrap><FlashQuestionsEmbedPlayer /></SuspenseWrap> },

      /* ── Student routes ── */
      { path: '/student', element: <Navigate to="/" replace /> },
      {
        path: '/student/favorites',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><StudentFavorites /></SuspenseWrap></ProtectedRoute>,
      },
      {
        path: '/student/profile',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><StudentProfile /></SuspenseWrap></ProtectedRoute>,
      },
      {
        path: '/student/decks/:deckId',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><StudentDeckPlayer /></SuspenseWrap></ProtectedRoute>,
      },
      {
        path: '/student/quizzes/:quizId',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><StudentQuizPlayer /></SuspenseWrap></ProtectedRoute>,
      },
      {
        path: '/student/train/session',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><TrainSession /></SuspenseWrap></ProtectedRoute>,
      },
      {
        path: '/student/journeys',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><StudentJourneyList /></SuspenseWrap></ProtectedRoute>,
      },
      {
        path: '/student/journeys/class/:classId',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><StudentJourneyList /></SuspenseWrap></ProtectedRoute>,
      },
      {
        path: '/student/journeys/tamkeen',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><JourneyTamkeenSession /></SuspenseWrap></ProtectedRoute>,
      },
      {
        path: '/student/journeys/:journeyId',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><StudentJourneyPage /></SuspenseWrap></ProtectedRoute>,
      },
      {
        path: '/student/journeys/:journeyId/step/:stepIndex',
        element: <ProtectedRoute allowedRoles={[ROLES.STUDENT]}><SuspenseWrap><JourneyStepRunner /></SuspenseWrap></ProtectedRoute>,
      },
    ],
  },
]);
